/* eslint-disable react/display-name */
/* eslint-disable @typescript-eslint/explicit-function-return-type */
import React, { useEffect } from 'react';
import { Typography } from '@material-ui/core';
import { useLazyQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { keyBy, merge } from 'lodash';
import { TFunction } from 'i18next';

import WDT_CLASSROOM_ERRORS_BY_CHARACTERS_QUERY from 'graph/reports/wdt-classroom-errors-by-characters-report-query';
import useReportQueryParams from '../../../../hooks/use-report-query-params';
import ErrorCount from '../../common/ErrorCount';
import ReportCard from 'components/ReportsModule/components/common/ReportCard';
import LwtTable, { ColumnProps } from 'components/@common/Table/LwtTable';
import { WdtCharacterErrorData } from 'types/schema-generated-types';
import { CharacterErrorType, ModuleType } from '../../../../types';
import { getQueryVariables } from '../../../../utils/report-param-utils';

const getColumns: (t: TFunction) => ColumnProps<any>[] = t => [
  {
    id: 'character',
    label: t('Character'),
    sortKey: 'character',
    getValue: item => <Typography>{item.character}</Typography>,
    sortable: true,
    summaryLabel: t('Total')
  },
  {
    id: 'missedStartingPointCount',
    label: t(CharacterErrorType.MISSED_STARTING_POINT, { ns: 'CharacterErrorType' }),
    getValue: item => <ErrorCount>{item.missedStartingPointCount}</ErrorCount>,
    sortable: true,
    sortKey: 'missedStartingPointCount',
    summaryKey: 'missedStartingPointCount'
  },
  {
    id: 'startedWrongWayCount',
    label: t(CharacterErrorType.STARTED_WRONG_WAY, { ns: 'CharacterErrorType' }),
    getValue: item => <ErrorCount>{item.startedWrongWayCount}</ErrorCount>,
    sortable: true,
    sortKey: 'startedWrongWayCount',
    summaryKey: 'startedWrongWayCount'
  },
  {
    id: 'outOfBoundsCount',
    label: t(CharacterErrorType.OUT_OF_BOUNDS, { ns: 'CharacterErrorType' }),
    getValue: item => <ErrorCount>{item.outOfBoundsCount}</ErrorCount>,
    sortable: true,
    sortKey: 'outOfBoundsCount',
    summaryKey: 'outOfBoundsCount'
  },
  {
    id: 'liftedFingerCount',
    label: t(CharacterErrorType.LIFTED_FINGER, { ns: 'CharacterErrorType' }),
    getValue: item => <ErrorCount>{item.liftedFingerCount}</ErrorCount>,
    sortable: true,
    sortKey: 'liftedFingerCount',
    summaryKey: 'liftedFingerCount'
  },
  {
    id: 'reversedDirectionCount',
    label: t(CharacterErrorType.REVERSED_DIRECTION, { ns: 'CharacterErrorType' }),
    getValue: item => <ErrorCount>{item.reversedDirectionCount}</ErrorCount>,
    sortable: true,
    sortKey: 'reversedDirectionCount',
    summaryKey: 'reversedDirectionCount'
  }
];

interface ErrorAnalysisForCharactersTableViewProps {
  module: ModuleType;
}

const ErrorAnalysisForCharactersTableView: React.FC<ErrorAnalysisForCharactersTableViewProps> = ({
  module
}) => {
  const [{ dateRange, entityType, entityId }] = useReportQueryParams();
  const [fetchReport, { data, loading }] = useLazyQuery(WDT_CLASSROOM_ERRORS_BY_CHARACTERS_QUERY);
  const { t } = useTranslation('Reports');
  const columns = getColumns(t);

  const reportData =
    data?.wdtClassroomErrorsByCharactersReport?.characterErrors?.map(
      ({ character, errors }: WdtCharacterErrorData) => {
        const errorsByType = keyBy(errors, 'type');

        return {
          character: character.character,
          missedStartingPointCount:
            errorsByType[CharacterErrorType.MISSED_STARTING_POINT]?.count ?? 0,
          startedWrongWayCount: errorsByType[CharacterErrorType.STARTED_WRONG_WAY]?.count ?? 0,
          outOfBoundsCount: errorsByType[CharacterErrorType.OUT_OF_BOUNDS]?.count ?? 0,
          liftedFingerCount: errorsByType[CharacterErrorType.LIFTED_FINGER]?.count ?? 0,
          reversedDirectionCount: errorsByType[CharacterErrorType.REVERSED_DIRECTION]?.count ?? 0
        };
      }
    ) ?? [];

  useEffect(() => {
    fetchReport({
      variables: merge(getQueryVariables({ entityId, entityType, dateRange }), {
        where: {
          module: module,
          characterOrder: 'natural'
        }
      })
    });
  }, [fetchReport, entityId, entityType, dateRange, module]);

  return (
    <ReportCard
      title={t(`Error for ${t(module, { ns: 'ModuleType' })}`)}
      loading={loading}
      reportData={reportData}
    >
      <LwtTable columns={columns} data={reportData} />
    </ReportCard>
  );
};

export default ErrorAnalysisForCharactersTableView;
